import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { spotlightPointerMove } from "../../utils/spotlight";
import { Link, useLocation } from "react-router-dom";
import type {
  GroupDailySpendSeriesResponse,
  MyGroupsDailySpendResponse,
  MyGroupsResponse,
  MyGroupUsageResponse,
  OrganisationPersonUsageResponse,
  OrganisationPersonUsageRow,
} from "../../api/organisationReporting";
import { isCurrentPeriod } from "../../hooks/useSelectedPeriod";
import { getVendorColor } from "../charts/chartColors";
import { formatDate, formatMoney, formatWholeMoney } from "../consumption/formatters";
import { PartialState } from "../states";
import card from "../tools/ToolCard.module.css";
import { GroupBurnUpChart } from "./GroupBurnUpChart";
import { AllowanceMeter, DailyToolBars, ToolSplit } from "./GroupParts";
import { computeGroupPace, type GroupPace } from "./groupPace";
import { peopleInsights } from "./groupPeople";
import { kindKeys, roleText, statusText, statusTones, summaryText } from "./groupText";
import { PeopleLimitChips } from "./PeopleLimitChips";
import cardStyles from "./GroupCard.module.css";
import styles from "./GroupDetailSummary.module.css";
import { Tooltip } from "../shared/Tooltip";

/** People shown before "Show everyone": enough to see who leads, without a wall of names. */
const PeopleShownAtFirst = 10;

interface Period {
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
}

/**
 * A group's own page, written for its owner: how the group stands against its allowance and where it is heading, its
 * month and its tools side by side, the groups beneath it ranked with each one's own status, and how its spend divides
 * among its people. The first group in `response.groups` is the one shown; the rest are the groups directly beneath it.
 * Rendered only once the page has a loaded response, so loading, error and denied are the page's job.
 *
 * @param props.response - The group and its children's spend.
 * @param props.dailySpend - The group's and its children's day-by-day series, or `null` when unavailable.
 * @param props.personUsage - Each person's spend, or `null` when unavailable.
 */
export function GroupDetailSummary({
  response,
  dailySpend,
  personUsage,
  canSeeEveryone = false,
}: {
  response: MyGroupsResponse;
  dailySpend: MyGroupsDailySpendResponse | null;
  personUsage: OrganisationPersonUsageResponse | null;
  /** Whether the viewer's scope is everyone (`scope.global`), so they may open anyone's usage, not only their own groups' people. */
  canSeeEveryone?: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [group, ...childGroups] = response.groups;
  if (!group) {
    return null;
  }

  const unsynced = response.vendorCompleteness.filter((vendor) => !vendor.hasEverSynced);
  const isCurrentMonth = isCurrentPeriod(response.periodStart);
  const period: Period = { periodStart: response.periodStart, periodEnd: response.periodEnd, generatedAt: response.generatedAt };
  const seriesFor = (groupId: string): GroupDailySpendSeriesResponse | null => dailySpend?.groups.find((series) => series.groupKind === groupId) ?? null;
  const paceOf = (target: MyGroupUsageResponse): GroupPace =>
    computeGroupPace(
      target.totalSpend ?? 0,
      target.currency === "USD" ? target.totalAllowance : null,
      isCurrentMonth ? (seriesFor(target.groupId)?.projection?.projectedTotal ?? null) : null,
      target.personCount,
      period,
      isCurrentMonth,
    );
  const series = seriesFor(group.groupId);
  const pace = paceOf(group);
  // A person's own usage opens only for someone the server will show it to: the owner of this group or of something above it,
  // someone whose scope is everyone, or someone looking in from the Organisation page (who may read anyone there).
  const canOpenPeople = group.isOwner || group.isWithinOwned || group.isOrganisationView === true || canSeeEveryone;

  return (
    <div className={styles.page} data-page-width="wide" onPointerMove={spotlightPointerMove}>
      {/* A never-synced vendor is a development-time fact, not something to show users: the notice is dev-only. */}
      {import.meta.env.DEV && unsynced.map((vendor) => (
        <PartialState key={vendor.vendorId} vendorName={vendor.vendorDisplayName} description={vendor.note ?? undefined} />
      ))}

      <Hero group={group} pace={pace} isCurrentMonth={isCurrentMonth} period={period} />

      {group.totalSpend === null ? (
        <p className={cardStyles.empty}>{t("myGroups.card.noSpend")}</p>
      ) : (
        <div className={styles.monthRow}>
          <section className={`${card.card} ${card.surface} ${styles.panel}`} aria-labelledby="group-month-heading" data-spotlight>
            <h2 id="group-month-heading" className={`panel-title ${styles.panelTitle}`}>
              {t("groupDetail.month.title")}
            </h2>
            <GroupBurnUpChart series={series} allowance={pace.allowance} currency={group.currency} period={period} />
            {series && series.days.length > 0 && <DailyToolBars group={group} series={series} />}
          </section>
          <section className={`${card.card} ${card.surface} ${styles.panel}`} aria-labelledby="group-tools-heading" data-spotlight>
            <h2 id="group-tools-heading" className={`panel-title ${styles.panelTitle}`}>
              {t("groupDetail.tools.title")}
            </h2>
            <ToolSplit group={group} compact={false} stacked />
          </section>
        </div>
      )}

      {childGroups.length > 0 && <ChildGroups parentName={group.groupLabel} groups={childGroups} paceOf={paceOf} />}

      {personUsage && personUsage.people.length > 0 && (
        <People usage={personUsage} groupId={group.groupId} groupName={group.groupLabel} canOpenPeople={canOpenPeople} />
      )}
    </div>
  );
}

/** The top of the page: what the group is, where it stands against its allowance, where it is heading, and the key figures. */
function Hero({ group, pace, isCurrentMonth, period }: { group: MyGroupUsageResponse; pace: GroupPace; isCurrentMonth: boolean; period: Period }): React.JSX.Element {
  const { t } = useTranslation();
  const currency = group.currency;
  const money = (amount: number): string => formatMoney(amount, currency);
  const over = pace.status === "heading" || pace.status === "over";

  return (
    <header className={`${card.card} ${card.surface} ${styles.hero}`} data-spotlight>
      <div className={cardStyles.top}>
        <div className={cardStyles.identity}>
          <div className={styles.path}>
            <span className={cardStyles.kind}>{t(kindKeys[group.kind])}</span>
            {group.breadcrumbs.length > 0 && <Breadcrumbs group={group} />}
          </div>
          <h1 className={`title-mark ${styles.title}`}>{group.groupLabel}</h1>
          <p className={cardStyles.role}>
            {roleText(t, group)} · {t("groupDetail.period", { start: formatDate(period.periodStart), end: formatDate(period.periodEnd) })}
          </p>
        </div>
        <div className={styles.heroChips}>
          {pace.status !== "none" && <span className={`${card.chip} ${statusTones[pace.status]} ${styles.heroChip}`}>{statusText(t, pace)}</span>}
          {/* Who is near or at a limit is listed with the group's people, further down. */}
          {(group.peopleAtLimit > 0 || group.peopleNearLimit > 0) && (
            <a href="#group-people" className={styles.chipLink}>
              <PeopleLimitChips reached={group.peopleAtLimit} near={group.peopleNearLimit} />
            </a>
          )}
        </div>
      </div>

      <p className={cardStyles.amount}>
        <span>{money(pace.spend)}</span>
        {pace.allowance !== null && <span className={cardStyles.ofAllowance}>{t("myGroups.card.ofAllowance", { amount: formatWholeMoney(pace.allowance, currency) })}</span>}
      </p>
      {!group.wasFullyConverted && <p className={cardStyles.note}>{t("myGroups.notFullyConverted")}</p>}
      {pace.allowance !== null && <AllowanceMeter pace={pace} />}
      <p className={styles.summary}>{summaryText(t, pace, currency)}</p>

      <dl className={styles.figures}>
        <div>
          <dt>{isCurrentMonth ? t("myGroups.card.projected") : t("groupDetail.figures.spent")}</dt>
          <dd className={over ? cardStyles.bad : undefined}>{isCurrentMonth ? (pace.projected !== null ? money(pace.projected) : "–") : money(pace.spend)}</dd>
        </div>
        <div>
          <dt>{isCurrentMonth ? t("groupDetail.figures.headroom") : t("myGroups.card.allowanceLeft")}</dt>
          <dd>
            {pace.allowance === null
              ? "–"
              : isCurrentMonth
                ? money(pace.headroomPerWorkingDay ?? 0)
                : money(Math.max(0, pace.allowance - pace.spend))}
          </dd>
        </div>
        <div>
          <dt>{t("myGroups.card.perPerson")}</dt>
          <dd>{pace.perPerson !== null ? money(pace.perPerson) : "–"}</dd>
        </div>
        <div>
          <dt>{t("myGroups.card.activePeople")}</dt>
          <dd>{group.personCount}</dd>
        </div>
      </dl>
    </header>
  );
}

/**
 * The units and groups above this one, outermost first. Each one the viewer can open links to its own page; the rest are
 * plain names, as are all of them when the server did not say which can be opened.
 */
function Breadcrumbs({ group }: { group: MyGroupUsageResponse }): React.JSX.Element {
  const { t } = useTranslation();
  const steps = group.ancestors ?? group.breadcrumbs.map((name) => ({ groupId: "", name, canOpen: false }));

  return (
    <nav aria-label={t("groupDetail.breadcrumbs")} className={styles.breadcrumbs}>
      {steps.map((step, index) => (
        <span key={step.groupId || `${String(index)}-${step.name}`} className={styles.crumb}>
          {index > 0 && (
            <span className={styles.crumbSeparator} aria-hidden="true">
              ›
            </span>
          )}
          {step.canOpen ? (
            <Link to={`/my-groups/${encodeURIComponent(step.groupId)}`} className={styles.crumbLink}>
              {step.name}
            </Link>
          ) : (
            <span>{step.name}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** The groups directly beneath this one, largest spend first, each with its own status, meter and figures, and a link to its page. */
function ChildGroups({
  parentName,
  groups,
  paceOf,
}: {
  parentName: string;
  groups: MyGroupUsageResponse[];
  paceOf: (group: MyGroupUsageResponse) => GroupPace;
}): React.JSX.Element {
  const { t } = useTranslation();
  const ranked = [...groups].sort((a, b) => (b.totalSpend ?? -1) - (a.totalSpend ?? -1));
  const needAttention = ranked.filter((child) => {
    const status = paceOf(child).status;
    return status === "heading" || status === "over";
  }).length;

  return (
    <section className={`${card.card} ${card.surface} ${styles.panel}`} aria-labelledby="group-children-heading" data-spotlight>
      <div className={styles.panelHead}>
        <h2 id="group-children-heading" className={`panel-title ${styles.panelTitle}`}>
          {t("groupDetail.children.title", { name: parentName })}
        </h2>
        <p className={styles.panelNote}>
          {needAttention > 0 ? t("groupDetail.children.attention", { count: needAttention }) : t("groupDetail.children.allFine", { count: ranked.length })}
        </p>
      </div>
      <ul className={styles.children}>
        {ranked.map((child) => {
          const pace = paceOf(child);
          return (
            <li key={child.groupId} className={styles.child}>
              <div className={styles.childName}>
                <Link to={`/my-groups/${encodeURIComponent(child.groupId)}`} className={cardStyles.titleLink}>
                  {child.groupLabel}
                </Link>
                <span className={styles.childPeople}>{t("myGroups.peopleCount", { count: child.personCount })}</span>
              </div>
              <div className={styles.childMeter}>
                {pace.allowance !== null ? <AllowanceMeter pace={pace} /> : <span className={styles.childNoAllowance}>{t("groupDetail.children.noAllowance")}</span>}
              </div>
              <div className={styles.childAmount}>
                <span>{child.totalSpend !== null ? formatMoney(child.totalSpend, child.currency) : "–"}</span>
                {pace.allowance !== null && <span className={styles.childOf}>{t("myGroups.card.ofAllowance", { amount: formatWholeMoney(pace.allowance, child.currency) })}</span>}
              </div>
              <div className={styles.childStatus}>
                {pace.status !== "none" && <span className={`${card.chip} ${statusTones[pace.status]}`}>{statusText(t, pace)}</span>}
                <PeopleLimitChips reached={child.peopleAtLimit} near={child.peopleNearLimit} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * How the group's spend divides among its people: a few plain findings (how concentrated it is, the typical person) and
 * everyone ranked by spend, each bar split by tool and drawn against the biggest spender's, so the shape reads at a glance.
 */
function People({
  usage,
  groupId,
  groupName,
  canOpenPeople,
}: {
  usage: OrganisationPersonUsageResponse;
  groupId: string;
  groupName: string;
  canOpenPeople: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const sectionRef = useRef<HTMLElement>(null);
  const isFlagged = (person: OrganisationPersonUsageRow): boolean => person.limit?.status === "reached" || person.limit?.status === "near";
  const flagged = usage.people.filter(isFlagged);
  const [showAll, setShowAll] = useState(false);
  const insights = peopleInsights(usage.people);

  // A link to the people (from My Groups, or the chips above) lands here once the list has drawn.
  useEffect(() => {
    if (location.hash === "#group-people") {
      sectionRef.current?.scrollIntoView({ block: "start" });
    }
  }, [location.hash]);
  // The list starts with the biggest spenders; anyone near or at a limit further down is shown too, in their place, so the
  // short list never hides them. Each keeps their rank by spend.
  const ranked = usage.people.map((person, index) => ({ person, rank: index + 1 }));
  const shown = showAll ? ranked : ranked.filter(({ person, rank }) => rank <= PeopleShownAtFirst || isFlagged(person));
  const largest = insights.largest > 0 ? insights.largest : 1;
  const money = (amount: number): string => formatMoney(amount, usage.currency);
  const vendorIndex = new Map(usage.vendors.map((vendor, index) => [vendor.vendorId, index]));
  // The key lists only the tools someone in this group used, in the portal's own order.
  const usedVendors = usage.vendors.filter((vendor) => usage.people.some((person) => person.byVendor.some((line) => line.vendorId === vendor.vendorId && (line.totalSpend ?? 0) > 0)));

  return (
    <section id="group-people" ref={sectionRef} className={`${card.card} ${card.surface} ${styles.panel} ${styles.peoplePanel}`} aria-labelledby="group-people-heading" data-spotlight>
      <div className={styles.panelHead}>
        <h2 id="group-people-heading" className={`panel-title ${styles.panelTitle}`}>
          {t("groupDetail.people.title")}
        </h2>
        <p className={styles.panelNote}>{t("groupDetail.people.count", { count: usage.people.length })}</p>
      </div>
      {flagged.length > 0 && (
        <p className={styles.limitNote}>
          <PeopleLimitChips
            reached={flagged.filter((person) => person.limit?.status === "reached").length}
            near={flagged.filter((person) => person.limit?.status === "near").length}
          />
        </p>
      )}

      <dl className={styles.insights}>
        <div>
          <dt>{t("groupDetail.people.topShare")}</dt>
          <dd>{`${String(insights.topShare)}%`}</dd>
          <dd className={styles.insightNote}>{t("groupDetail.people.topShareNote", { name: insights.topName })}</dd>
        </div>
        <div>
          <dt>{t("groupDetail.people.topFifthShare")}</dt>
          <dd>{`${String(insights.topFifthShare)}%`}</dd>
          <dd className={styles.insightNote}>{t("groupDetail.people.topFifthShareNote", { count: insights.topFifthCount })}</dd>
        </div>
        <div>
          <dt>{t("groupDetail.people.median")}</dt>
          <dd>{money(insights.median)}</dd>
          <dd className={styles.insightNote}>{t("groupDetail.people.medianNote", { average: money(insights.average) })}</dd>
        </div>
      </dl>

      <ul className={styles.key} aria-label={t("groupDetail.people.key")}>
        {usedVendors.map((vendor) => (
          <li key={vendor.vendorId}>
            <span className={cardStyles.swatch} style={{ background: getVendorColor(vendor.vendorId, vendorIndex.get(vendor.vendorId) ?? 0) }} aria-hidden="true" />
            {vendor.vendorDisplayName}
          </li>
        ))}
      </ul>

      <ol className={styles.people}>
        {shown.map(({ person, rank }) => {
          const total = person.totalSpend ?? 0;
          const share = insights.total > 0 ? Math.round((total / insights.total) * 100) : 0;
          const spent = person.byVendor.filter((line) => (line.totalSpend ?? 0) > 0);
          return (
            <li key={person.personId} className={styles.person}>
              <span className={styles.rank}>{rank}</span>
              <span className={styles.personWho}>
                {canOpenPeople ? (
                  <Tooltip label={t("personUsage.openUsage", { name: person.displayName })}>
                    <Link
                      to={`/my-groups/${encodeURIComponent(groupId)}/people/${encodeURIComponent(person.personId)}`}
                      state={{ groupName }}
                      className={`${styles.personName} ${styles.personLink}`}
                    >
                      {person.displayName}
                    </Link>
                  </Tooltip>
                ) : (
                  <Tooltip label={person.displayName}>
                    <span className={styles.personName}>
                      {person.displayName}
                    </span>
                  </Tooltip>
                )}
                {(person.limit?.status === "reached" || person.limit?.status === "near") && (
                  <Tooltip
                    label={t("groupDetail.people.limitDetail", {
                      tool: usedVendors.find((vendor) => vendor.vendorId === person.limit?.vendorId)?.vendorDisplayName ?? person.limit.vendorId,
                      spend: money(person.limit.spend),
                      limit: formatWholeMoney(person.limit.limitUsd, usage.currency),
                    })}
                  >
                    <span className={`${card.chip} ${person.limit.status === "reached" ? card.chipDanger : card.chipWarning} ${styles.personLimit}`}>
                      {t(person.limit.status === "reached" ? "groupDetail.people.limitReached" : "groupDetail.people.limitNear", {
                        percent: Math.floor(person.limit.percentUsed),
                      })}
                    </span>
                  </Tooltip>
                )}
              </span>
              <span className={styles.personBar} aria-hidden="true">
                <span style={{ width: `${String((total / largest) * 100)}%` }}>
                  {spent.map((line) => (
                    <span
                      key={line.vendorId}
                      style={{ width: `${String(total > 0 ? ((line.totalSpend ?? 0) / total) * 100 : 0)}%`, backgroundColor: getVendorColor(line.vendorId, vendorIndex.get(line.vendorId) ?? 0) }}
                    />
                  ))}
                </span>
              </span>
              <span className="visually-hidden">
                {spent.map((line) => `${usedVendors.find((vendor) => vendor.vendorId === line.vendorId)?.vendorDisplayName ?? line.vendorId} ${money(line.totalSpend ?? 0)}`).join(", ")}
              </span>
              <span className={styles.personAmount}>{person.totalSpend !== null ? money(total) : "–"}</span>
              <span className={styles.personShare}>{`${String(share)}%`}</span>
            </li>
          );
        })}
      </ol>

      {usage.people.length > PeopleShownAtFirst && (
        <button type="button" className={`button button-secondary ${styles.showAll}`} onClick={() => setShowAll((value) => !value)} aria-expanded={showAll}>
          {showAll ? t("groupDetail.people.showFewer") : t("groupDetail.people.showAll", { count: usage.people.length })}
        </button>
      )}
    </section>
  );
}
